/** The views F1 reads, through the shared cache. Only the board is essential; every other view degrades to "nothing to show". */
import type { CoachTurnView, CohortBoard, ContentCatalog, ErrorBody, MarketView, NarrativeReviewItem, RosterMember } from "@bsim/contracts";
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRoute } from "../api/context";
import { bandRound } from "./derive";
import { buildQueue, type QueueItem } from "./queue";

export interface BoardData {
  readonly board: CohortBoard | undefined;
  readonly error: ErrorBody["error"] | undefined;
  readonly market: MarketView | undefined;
  readonly roster: readonly RosterMember[];
  readonly catalog: ContentCatalog | undefined;
  readonly queue: readonly QueueItem[];
  /** Everyone called to the pre-check: the api's record plus what this page just sent. */
  readonly calledIn: readonly string[];
  readonly heldNarratives: readonly NarrativeReviewItem[];
  readonly flaggedTurns: readonly CoachTurnView[];
}

/** The stream can be open and silent (a proxy that drops it): presence and the board then read as stale. Past 30 s without an update, the board and roster are fetched again, and keep being while it stays silent. */
export const PRESENCE_SILENT_MS = 30_000;
function usePresenceFallback(cohortId: string): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    const id = setInterval(() => {
      const board = queryClient.getQueryState(["cohortBoard", { cohortId }]);
      if (board?.dataUpdatedAt !== undefined && Date.now() - board.dataUpdatedAt >= PRESENCE_SILENT_MS) {
        void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "cohortBoard" || q.queryKey[0] === "rosterList" });
      }
    }, 10_000);
    return () => clearInterval(id);
  }, [cohortId, queryClient]);
}

/** The last value a read returned: while it is fetched again (or fails once) the board keeps showing it, never an empty state. */
export function useLastGood<T>(value: T | undefined, key: string): T | undefined {
  const last = useRef<{ readonly key: string; readonly value: T } | undefined>(undefined);
  if (value !== undefined) last.current = { key, value };
  return value ?? (last.current?.key === key ? last.current.value : undefined);
}

export function useBoardData(cohortId: string, calledIn: readonly string[] = []): BoardData {
  usePresenceFallback(cohortId);
  const params = { cohortId };
  const boardRead = useRoute("cohortBoard", params);
  const board = { data: useLastGood(boardRead.data, cohortId), error: boardRead.error };
  const marketRead = useRoute("publicMarket", params);
  const market = { data: useLastGood(marketRead.data, cohortId) };
  const roster = useRoute("rosterList", params);
  const catalog = useRoute("contentCatalog", params, { staleTime: Infinity });
  const exceptions = useRoute("exceptionList", params, { query: { status: "pending" } });
  const round = board.data === undefined ? null : bandRound(board.data);
  const narratives = useRoute("narrativeList", round === null ? null : { cohortId, roundId: round.id });
  const coach = useRoute("coachLog", params, { query: { flagged: true } });

  const heldNarratives = (narratives.data?.items ?? []).filter((n) => n.reviewState === "pending_facilitator");
  const flaggedTurns = coach.data?.items ?? [];
  const lastRoster = useLastGood(roster.data, cohortId);
  const members = lastRoster?.members ?? [];
  // Who was called in: the api's own record (it survives a reload and reaches every facilitator) and what this page just sent.
  const called = [...new Set([...(board.data?.prePost.preCalledInUserIds ?? []), ...calledIn])];
  const queue = board.data === undefined ? [] : buildQueue({ board: board.data, exceptions: exceptions.data?.items ?? [], heldNarratives, flaggedTurns, members, calledIn: called, postCheckOpen: board.data.cohort.postAssessmentOpenedAt !== null });
  return {
    board: board.data, error: board.error?.result.error, market: market.data, roster: members, catalog: catalog.data, queue, heldNarratives, flaggedTurns, calledIn: called,
  };
}
